/**
 * Recuperação automática de "ChunkLoadError" após deploy.
 *
 * Quando publicamos uma nova versão, os arquivos JS antigos deixam de existir
 * no CDN. Uma aba aberta que tenta carregar uma rota lazy quebra com
 * "Failed to fetch dynamically imported module". A recuperação correta é
 * recarregar a página uma única vez (guardamos a marca em sessionStorage para
 * nunca entrar em loop de reload).
 */

const RELOAD_FLAG = "zappy:chunk-reload-at";
const RELOAD_COOLDOWN_MS = 60_000;

const CHUNK_ERROR_PATTERNS = [
  "failed to fetch dynamically imported module",
  "error loading dynamically imported module",
  "importing a module script failed",
  "chunkloaderror",
  "loading chunk",
  "loading css chunk",
  "unable to preload css",
];

export function isChunkLoadError(value: unknown): boolean {
  const message =
    value instanceof Error
      ? `${value.name} ${value.message}`
      : typeof value === "string"
        ? value
        : "";
  if (!message) return false;
  const normalized = message.toLowerCase();
  return CHUNK_ERROR_PATTERNS.some((pattern) => normalized.includes(pattern));
}

function recentlyReloaded(): boolean {
  try {
    const raw = sessionStorage.getItem(RELOAD_FLAG);
    if (!raw) return false;
    const at = Number(raw);
    if (!Number.isFinite(at)) return false;
    return Date.now() - at < RELOAD_COOLDOWN_MS;
  } catch {
    return false;
  }
}

/** Recarrega uma única vez para buscar o manifesto novo. */
export function reloadForNewDeploy(): boolean {
  if (typeof window === "undefined") return false;
  if (recentlyReloaded()) return false;
  try {
    sessionStorage.setItem(RELOAD_FLAG, String(Date.now()));
  } catch {
    /* storage indisponível — segue com o reload mesmo assim */
  }
  window.location.reload();
  return true;
}

/** Escuta erros globais e recarrega quando forem de chunk ausente. */
export function installChunkReloadHandler(): void {
  if (typeof window === "undefined") return;

  const handle = (reason: unknown) => {
    if (isChunkLoadError(reason)) reloadForNewDeploy();
  };

  window.addEventListener("error", (event) => {
    handle(event.error ?? event.message);
  });
  window.addEventListener("unhandledrejection", (event) => {
    handle((event as PromiseRejectionEvent).reason);
  });
}
